import { useMemo } from "react"
import { Link } from "react-router-dom"
import { Flame, Gauge, Lightbulb, Clock } from "lucide-react"
import { DateTime } from "luxon"
import { useFetchLeadScoresQuery } from "../../../../redux/queries"
import { useAppSelector } from "../../../hooks"
import {
  selectUserDetails,
  selectUserRoles
} from "../../../../redux/slices/auth"
import { hasUserRole, getUserBrandId } from "../../../utils"
import { LeadScore } from "../../../../types"
import If from "../../if"
import ScoreCircle from "../../lead-scoring/score-circle"
import {
  CATEGORY_CONFIG,
  cleanSuggestedAction
} from "../../lead-scoring/category-helpers"
import ActivitySkeleton from "../../skeletons/dashboard/activity"

const MAX_PREVIEW_ITEMS = 5

const LeadScorePreviewCard = ({
  score,
  index
}: {
  score: LeadScore
  index: number
}) => {
  const config = CATEGORY_CONFIG[score.category]
  const sub = score.subscription
  const displayName =
    sub?.first_name && sub?.last_name
      ? `${sub.first_name} ${sub.last_name}`
      : sub?.email ?? ""
  const scoredAtTimestamp = Number(score.scored_at)
  const scoredAtRelative =
    scoredAtTimestamp && !isNaN(scoredAtTimestamp)
      ? DateTime.fromSeconds(scoredAtTimestamp).toRelative() ?? ""
      : ""

  return (
    <Link
      to={`/engagements/${score.subscription_id}`}
      className="flex items-start gap-3 py-3 px-2 hover:bg-slate-50 rounded-lg transition-colors cursor-pointer group"
    >
      <ScoreCircle score={Number(score.intent_score)} index={index} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-slate-800 truncate">
            {displayName}
          </span>
          <span className={`badge ${config.badgeClass} text-[10px]`}>
            {config.label}
          </span>
          <span className="flex items-center gap-1 text-[10px] text-neutral-400 ml-auto shrink-0">
            <Clock size={10} />
            {scoredAtRelative}
          </span>
        </div>
        <p className="text-xs text-slate-500 leading-snug line-clamp-1">
          {score.summary}
        </p>
        <div className="flex items-center gap-1 mt-1 text-xs text-primary-500">
          <Lightbulb size={10} />
          <span className="line-clamp-1">
            {cleanSuggestedAction(score.suggested_action)}
          </span>
        </div>
      </div>
    </Link>
  )
}

const DashboardLeadScoringPreview = () => {
  const userDetails = useAppSelector(selectUserDetails)
  const userRoles = useAppSelector(selectUserRoles)
  const isBankAdmin = hasUserRole(userRoles, "bank admin")
  const isSuperAdmin = hasUserRole(userRoles, "administrator")
  const isLo = hasUserRole(userRoles, "loan officer")

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean | undefined> = {
      limit: MAX_PREVIEW_ITEMS,
      min_score: 50
    }
    if (!isSuperAdmin && !isBankAdmin && isLo && userDetails?.id) {
      params.loid = userDetails.id
    } else if (
      !isSuperAdmin &&
      isBankAdmin &&
      userDetails?.id &&
      userDetails?.org
    ) {
      const brandId =
        getUserBrandId(userDetails.org, userDetails.id) ??
        userDetails.org?.brands?.[0]?.id ??
        userDetails?.brand_id
      if (brandId) params.brand_id = brandId
    }
    return params
  }, [userDetails, isSuperAdmin, isBankAdmin, isLo])

  const { data: response, isFetching } = useFetchLeadScoresQuery(queryParams)
  const scores = response?.data ?? []
  const hotCount =
    (response?.stats?.hot_lead ?? 0) + (response?.stats?.needs_contact ?? 0)

  return (
    <If
      condition={isFetching && !response}
      then={<ActivitySkeleton />}
      else={
        <div className="bg-white rounded-md py-4 md:pt-6 md:pb-4">
          <div className="px-4 md:px-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Gauge size={18} className="text-neutral-500" />
                <h3 className="font-normal text-base">
                  Scoreboard{" "}
                  <span className="text-xs font-normal text-neutral-400">
                    (Preview)
                  </span>
                </h3>
              </div>
              {hotCount > 0 && (
                <span className="flex items-center gap-1 badge badge-danger">
                  <Flame size={12} />
                  {hotCount} high intent
                </span>
              )}
            </div>
            <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
              {scores.length > 0 ? (
                <div className="divide-y divide-slate-50">
                  {scores.map((score, i) => (
                    <LeadScorePreviewCard
                      key={score.id}
                      score={score}
                      index={i}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-sm text-neutral-400">
                  No high-intent leads right now
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end font-medium border-t border-gray-200 pt-4 pr-4">
            <Link className="text-primary-400 text-sm" to="/lead-scoring">
              View all Scores
            </Link>
          </div>
        </div>
      }
    />
  )
}

export default DashboardLeadScoringPreview
